import { HotmartRum } from '@hot-observability-js/react';
import { TOAST_TYPE } from 'components/cosmos/toast/constants';
import { Polyglot } from 'components/translate';
import {
  normalizeDrippingData,
  normalizeDrippingDataFromMask
} from 'helpers/Dripping';
import { htmlStringIsEmpty, rawTextLength } from 'helpers/String';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { action, extendObservable, toJS } from 'mobx';
import {
  DrippingFormStore,
  DrippingStore
} from 'modules/content/dripping/stores';
import drippingPayloadUtil from 'modules/content/dripping/utils/drippingPayloadUtil';
import drippingResponseUtil from 'modules/content/dripping/utils/drippingResponseUtil';
import { EmptyQuestionError } from 'modules/content/quiz/QuizErrors';
import QuizUtils from 'modules/content/quiz/QuizUtils';
import { FileService } from 'modules/file';
import { UiService } from 'modules/ui';

import Store from './QuizStore';

import { uploadNotifySave } from '@hotmart/club-files-ui';
import Request from '@hotmart/request';

const DEFAULT_OBJECT = {};
const DEFAULT_ARRAY = [];

class QuizService {
  // #region setup
  @action
  clear() {
    Object.assign(Store, {
      id: null,
      configuration: {
        certificate: null,
        certificateActive: false,
        certificateReleasePercentage: 0,
        maxQuestionToRandom: 5,
        minimumScorePercent: 0,
        minimumScoreRequired: false,
        randomAnswerOrder: false,
        randomQuestion: false,
        retry: false,
        retryInterval: null,
        showAnswersAfterResult: false
      },
      page: {
        hasDuration: false,
        liberationDate: null,
        liberationDays: 0,
        liberationType: null,
        name: '',
        content: ' ',
        published: false,
        pageLanguage: {
          language: '',
          signLanguage: false
        }
      },
      profiles: [],
      questions: [
        {
          answers: [
            {
              correct: true,
              id: 'new_a',
              order: 1,
              text: ''
            }
          ],
          id: 'new_q',
          mandatory: false,
          order: 1,
          text: ''
        }
      ],
      type: 'EXERCISE',
      snapshot: {}
    });
  }

  @action
  setData(data = {}) {
    const { configuration, ...quizData } = data;

    if (!isEmpty(configuration)) {
      extendObservable(Store.configuration, configuration);
    }

    extendObservable(Store, quizData);
  }
  // #endregion

  // #region page actions
  @action
  async getQuiz(pageId) {
    try {
      const response = await Request('quiz.get', { id: pageId });

      response.profiles = await this.getQuizProfileImages(response.profiles);
      DrippingFormStore.setQuizRequiresMinimumScore(
        Boolean(response.configuration?.minimumScoreRequired)
      );

      if (response.questions.length) {
        const drippings = drippingResponseUtil.getNormalizedItems(
          response.page.drippingConfigs || []
        );

        DrippingStore.setItems(drippings, false);

        // TODO: melhorar a solução do Prevent Leave da página.
        delete response.page.drippingConfigs;
        delete response.page.classesAvailableForDripping;

        this.setData(response);
      } else {
        this.name = response.page.name;
        this.quizId = response.page.id;
      }

      this.snapshot = this.current;
    } catch (error) {
      UiService.handleError(error);
    }
  }

  @action
  // eslint-disable-next-line complexity
  async save() {
    try {
      const data = this.checkSaveData();
      const pageLanguage = {
        language: data?.page?.pageLanguage?.language || '',
        signLanguage: Boolean(data?.page?.pageLanguage?.signLanguage)
      };

      if (data) {
        data.page = {
          ...normalizeDrippingData(data.page),
          pageLanguage: pageLanguage?.language ? pageLanguage : null,
          ...drippingPayloadUtil.getPayload(!this.quizId)
        };
        data.configuration = this.normalizeConfigurationFromMask(
          data.configuration
        );

        if (data.snapshot) {
          delete data.snapshot;
        }

        const method = this.quizId ? 'quiz.save' : 'quiz.create';
        const response = await Request(method, {
          quizId: this.quizId,
          ...data
        });

        this.quizId = response.page ? response.page.id : response.id;

        UiService.setAlertSuccess('quiz.save_success');
        this.snapshot = this.current;

        uploadNotifySave(this.quizId);

        return response;
      }
      return false;
    } catch (error) {
      const newError = {
        ...error,
        data: {
          ...error.data,
          errorDetail: {
            ...error.data.errorDetail,
            ...(error.data.errorDetail['questions[].text'] && {
              questions: error.data.errorDetail['questions[].text']
            })
          }
        }
      };

      delete newError?.data?.errorDetail['questions[].text'] &&
        newError?.data?.errorDetail['questions[].text'];

      UiService.handleError(newError);
      return false;
    }
  }

  async getQuizProfileImages(profiles) {
    const profileImages = [...Store.profileImages];

    const imagePromises = profiles.map(async (profile) => {
      const images = await FileService.fetchQuizProfile(profile.id);
      const image = images[0] || {};
      const presaveProfile =
        Store.profiles.find(
          (item) => profile.answerOrder === item.answerOrder
        ) || {};
      const temporaryId = presaveProfile.temporaryId;

      image.id && profileImages.push({ id: image.id, profileId: profile.id });
      return { ...profile, imageId: image.id, temporaryId };
    });

    const profilesWithImages = await Promise.all(imagePromises);

    Store.profileImages = profileImages;
    return profilesWithImages;
  }
  // #endregion

  // #region getters
  get quizId() {
    return Store.page.id;
  }

  set quizId(id) {
    Store.page.id = id;
  }

  get published() {
    return Store.page.published;
  }

  set published(status) {
    Store.page.published = status;
  }

  get type() {
    return Store.type;
  }

  set type(type) {
    Store.type = type;
    if (this.isProfile()) {
      Store.configuration.minimumScoreRequired = false;
    }

    if (this.isProfile() && !this.profiles.length) {
      this.addEmptyProfile();
      this.addEmptyQuestion();
    } else if (!this.isProfile() && this.profiles.length) {
      Store.profiles = [];
    }
  }

  get name() {
    return Store.page.name;
  }

  set name(name) {
    Store.page.name = name;
  }

  get content() {
    return Store.page.content;
  }

  set content(content) {
    Store.page.content = content;
  }

  get liberationDays() {
    return Store.page.liberationDays;
  }

  set liberationDays(liberationDays) {
    Store.page.liberationDays = liberationDays;
  }

  get liberationType() {
    return Store.page.liberationType;
  }

  set liberationType(liberationType) {
    Store.page.liberationType = liberationType;
  }

  get hasDuration() {
    return Store.page.hasDuration;
  }

  set hasDuration(hasDuration) {
    Store.page.hasDuration = hasDuration;
  }

  get liberationDate() {
    return Store.page.liberationDate;
  }

  set liberationDate(liberationDate) {
    Store.page.liberationDate = liberationDate;
  }

  get durationDays() {
    return Store.page.durationDays;
  }

  set durationDays(durationDays) {
    Store.page.durationDays = durationDays;
  }

  get profiles() {
    return Store.profiles || [];
  }

  get profileImages() {
    return Store.profileImages;
  }

  get newId() {
    return QuizUtils.generateNewId();
  }

  get page() {
    const {
      id,
      moduleId,
      name,
      content,
      published,
      liberationType,
      liberationDays,
      liberationDate,
      hasDuration,
      durationDays
    } = Store.page;

    const page = normalizeDrippingDataFromMask(
      cloneDeep({
        id,
        moduleId,
        name,
        content,
        published,
        liberationType,
        liberationDays,
        liberationDate,
        hasDuration,
        durationDays
      })
    );

    if (htmlStringIsEmpty(page.content)) {
      page.content = '';
    }

    return page;
  }

  get current() {
    const { configuration, profiles, questions, type } = Store;
    const current = cloneDeep({
      configuration,
      page: this.page,
      profiles: toJS(profiles),
      questions: toJS(questions),
      type
    });

    current.configuration = this.normalizeConfigurationFromMask(
      current.configuration
    );
    current.questions = current.questions.map(this.removeQuestionsIds);

    return current;
  }

  get snapshot() {
    return Store.snapshot;
  }

  set snapshot(value) {
    value.questions = value.questions.map(this.removeQuestionsIds);

    Store.snapshot = value;
  }

  get language() {
    return Store.page.pageLanguage.language;
  }

  set language(value) {
    Store.page.pageLanguage = {
      ...Store?.page?.pageLanguage,
      language: value
    };
  }

  get signLanguage() {
    return Store.page.pageLanguage.signLanguage;
  }

  set signLanguage(value) {
    Store.page.pageLanguage = {
      ...Store?.page?.pageLanguage,
      signLanguage: value
    };
  }

  // #endregion

  // #region setters

  set minimumScoreRequired(status) {
    Store.configuration.minimumScoreRequired = status;
    if (status) {
      Store.configuration.retry = status;
      Store.configuration.showAnswersAfterResult = false;
    }
  }

  set minimumScorePercent(value) {
    Store.configuration.minimumScorePercent = value;
  }

  set retryInterval(value) {
    Store.configuration.retryInterval = value;
  }

  set retry(status) {
    Store.configuration.retry = status;
  }

  set showAnswersAfterResult(status) {
    Store.configuration.showAnswersAfterResult = status;
  }

  set randomQuestion(status) {
    Store.configuration.randomQuestion = status;
  }

  set maxQuestionToRandom(value) {
    Store.configuration.maxQuestionToRandom = value;
  }

  set randomAnswerOrder(status) {
    Store.configuration.randomAnswerOrder = status;
  }

  set moduleId(moduleId) {
    Store.page.moduleId = moduleId;
  }

  set certificateActive(status) {
    Store.configuration.certificateActive = status;
  }

  set certificateReleasePercentage(value) {
    Store.configuration.certificateReleasePercentage = value;
  }

  set certificate(value) {
    Store.configuration.certificate = value;
  }

  getQuestionById(questionId) {
    const { questions } = Store;
    const questionIndex = questions.findIndex((item) => item.id === questionId);
    const question = questions[questionIndex];

    return {
      question: question || DEFAULT_OBJECT,
      questionIndex
    };
  }

  @action
  toggleRequiredQuestion(index, status) {
    const questions = Store.questions;

    questions[index].mandatory = status;
    Store.questions = [...questions];
  }

  @action
  changeCorrectAnswer(answerIndex, questionId) {
    const { questions } = Store;
    const { question, questionIndex } = this.getQuestionById(questionId);

    if (isEmpty(question)) {
      return;
    }

    let { answers = DEFAULT_ARRAY } = question;

    answers = answers.map((answer, index) => ({
      ...answer,
      correct: index === answerIndex
    }));

    const updatedQuestion = {
      ...question,
      answers
    };

    Store.questions = [
      ...questions.slice(0, questionIndex),
      updatedQuestion,
      ...questions.slice(questionIndex + 1)
    ];
  }

  @action
  updateProfileContent(index, type, content) {
    const profiles = Store.profiles;

    profiles[index][type] = content;
    Store.profiles = [...profiles];
  }

  @action
  updateQuizQuestions(questions) {
    Store.questions = [...questions];
  }

  @action
  updateQuizProfiles(profiles) {
    Store.profiles = [...profiles];
  }

  @action
  updateQuizImages(profileImages) {
    Store.profileImages = [...profileImages];
  }

  updateProfileImage(profileId, fileId) {
    const profiles = Store.profiles;
    const profileIndex = profiles.findIndex(
      (profile) => profile.id === profileId
    );

    if (profiles[profileIndex]) {
      const updatedProfile = {
        ...profiles[profileIndex],
        imageId: fileId
      };

      Store.profiles = [
        ...profiles.slice(0, profileIndex),
        updatedProfile,
        ...profiles.slice(profileIndex + 1)
      ];
    }
  }

  @action
  updateQuizContent(id, questionId, text) {
    const questions = Store.questions;

    if (questionId) {
      const questionIndex = questions.findIndex(
        (item) => item.id === questionId
      );
      const answerIndex = questions[questionIndex].answers.findIndex(
        (item) => item.id === id
      );

      questions[questionIndex].answers[answerIndex].text = text;
    } else {
      const questionIndex = questions.findIndex((item) => item.id === id);

      questions[questionIndex].text = text;
    }

    Store.questions = [...questions];
  }
  // #endregion

  // #region helpers
  removeQuestionsIds = (question) => {
    delete question.id;

    question.answers.map((answer) => {
      delete answer.id;
      return answer;
    });

    return question;
  };

  normalizeConfigurationFromMask(configuration) {
    const { maxQuestionToRandom, minimumScorePercent, retryInterval } =
      configuration;

    if (maxQuestionToRandom) {
      configuration.maxQuestionToRandom = parseInt(maxQuestionToRandom);
    }

    if (minimumScorePercent) {
      configuration.minimumScorePercent = parseInt(minimumScorePercent);
    }

    if (retryInterval) {
      configuration.retryInterval = parseInt(retryInterval);
    }

    return configuration;
  }

  checkDescription(maxDescriptionLen) {
    const content = Store.page.content;
    const textLength = rawTextLength(content);

    if (textLength > maxDescriptionLen) {
      UiService.setAlertError('quiz.max_description', {
        limit: maxDescriptionLen
      });
      return false;
    }

    return true;
  }

  checkAnswersQuantity() {
    const questions = Store.questions;

    if (this.isProfile() && questions.length) {
      questions.map((question) => {
        if (question.answers.length < this.profiles.length) {
          const diff = this.profiles.length - question.answers.length;

          for (let index = 0; index < diff; index++) {
            question.answers.push({
              correct: false,
              id: this.newId,
              order: question.answers.length,
              text: ''
            });
          }
        }
        return question;
      });

      Store.questions = [...questions];
    }
  }

  handleEmptyQuestionsError() {
    UiService.setAlertWithText({
      type: TOAST_TYPE.danger,
      message: Polyglot.t('alerts.quiz.empty_questions')
    });
  }

  handleEmptyAnswersError(emptyQuestions, emptyAnswers) {
    const questionMarkers = emptyQuestions.map(({ index }) =>
      QuizUtils.getQuestionMarker(index)
    );
    const answerMarkers = emptyAnswers.map(({ index, questionIndex }) =>
      QuizUtils.getAnswerMarker(questionIndex, index)
    );
    const markers = questionMarkers.concat(answerMarkers).join(', ');
    const message = `${Polyglot.t('alerts.quiz.empty_questions')} (${markers})`;

    UiService.setAlertWithText({
      type: TOAST_TYPE.danger,
      message
    });
    HotmartRum.captureException(
      new EmptyQuestionError(emptyQuestions, emptyAnswers)
    );
  }

  checkSaveData() {
    const data = JSON.parse(JSON.stringify(Store));
    const questions = data.questions;
    const profiles = data.profiles;
    const emptyQuestions = [];
    const emptyAnswers = [];

    if (questions.length === 0) {
      this.handleEmptyQuestionsError();
      return false;
    }

    questions.forEach((question, questionIndex) => {
      if (question.id.indexOf('new_') > -1) {
        question.id = null;
      }
      if (QuizUtils.textIsEmpty(question.text)) {
        emptyQuestions.push({
          index: questionIndex,
          text: question.text
        });
      }
      question.answers.forEach((answer, answerIndex) => {
        if (answer.id.indexOf('new_') > -1) {
          answer.id = null;
        }
        if (QuizUtils.textIsEmpty(answer.text)) {
          emptyAnswers.push({
            questionIndex,
            index: answerIndex,
            text: answer.text
          });
        }
      });
    });

    if (emptyQuestions.length + emptyAnswers.length > 0) {
      this.handleEmptyAnswersError(emptyQuestions, emptyAnswers);
      return false;
    }

    if (profiles.length) {
      profiles.map((profile) => {
        if (profile.id.indexOf('new_') > -1) {
          profile.id = null;
        }
        return profile;
      });
    }

    return data;
  }

  addEmptyProfile() {
    const newId = this.newId;

    Store.profiles = [
      {
        id: newId,
        temporaryId: newId,
        name: '',
        description: '',
        answerOrder: 1
      }
    ];
  }

  addEmptyQuestion() {
    Store.questions = [
      {
        answers: [
          {
            correct: true,
            id: this.newId,
            order: 1,
            text: ''
          }
        ],
        id: this.newId,
        mandatory: false,
        order: 1,
        text: ''
      }
    ];
  }

  @action
  isProfile() {
    return Store.type === 'PROFILE';
  }

  @action
  hasProfile() {
    if (!this.profiles.length) {
      return false;
    } else {
      return !this.checkEmptyProfile();
    }
  }

  checkEmptyProfile() {
    return this.profiles.some((profile) => {
      return (
        QuizUtils.textIsEmpty(profile.name) ||
        QuizUtils.textIsEmpty(profile.description)
      );
    });
  }

  @action
  addQuestion(index) {
    const questions = Store.questions;
    const answers = [];
    let answersQuantity = 1;

    if (this.isProfile()) {
      answersQuantity = Store.profiles.length;
    }

    for (let index = 0; index < answersQuantity; index++) {
      const newAnswer = {
        correct: index === 0,
        id: this.newId,
        order: index + 1,
        text: ''
      };

      answers.push(newAnswer);
    }

    const newQuestion = {
      answers,
      id: this.newId,
      text: '',
      mandatory: false,
      order: null
    };

    questions.splice(index + 1, 0, newQuestion);
    questions.map((item, i) => (item.order = i + 1));

    Store.questions = [...questions];
    return index + 1;
  }

  @action
  addAnswer(questionId) {
    const { questions } = Store;
    const { question, questionIndex } = this.getQuestionById(questionId);

    if (isEmpty(question)) {
      return;
    }

    let { answers = DEFAULT_ARRAY } = question;

    const newAnswer = {
      correct: false,
      id: this.newId,
      order: answers.length,
      text: ''
    };

    answers = [...answers, newAnswer];

    const updatedQuestion = {
      ...question,
      answers
    };

    Store.questions = [
      ...questions.slice(0, questionIndex),
      updatedQuestion,
      ...questions.slice(questionIndex + 1)
    ];

    return newAnswer.id;
  }

  @action
  removeAnswer(answerIndex, questionId) {
    const { questions } = Store;
    const { question, questionIndex } = this.getQuestionById(questionId);
    let { answers = DEFAULT_ARRAY } = question;
    const answer = answers[answerIndex];

    if (isEmpty(question) || !answer) {
      return;
    }

    const { correct: removedCorrect } = answer;

    answers = [
      ...answers.slice(0, answerIndex),
      ...answers.slice(answerIndex + 1)
    ];

    if (removedCorrect) {
      answers[0].correct = true;
    }

    const updatedQuestion = {
      ...question,
      answers
    };

    Store.questions = [
      ...questions.slice(0, questionIndex),
      updatedQuestion,
      ...questions.slice(questionIndex + 1)
    ];
  }

  @action
  checkEmptyAnswers(questionId) {
    let isEmpty = false;
    const index = [];

    if (questionId) {
      const questionIndex = Store.questions.findIndex(
        (item) => item.id === questionId
      );

      if (questionIndex) {
        const answers = Store.questions[questionIndex].answers;

        answers.forEach((answer) => {
          if (QuizUtils.textIsEmpty(answer.text)) {
            isEmpty = true;
            index.push(answer.id);
          }
        });
      }
    }

    return { empty: isEmpty, index: index[0] };
  }

  @action
  removeQuestion(id) {
    const questions = Store.questions.filter((item) => item.id !== id);

    questions.map((item, i) => (item.order = i + 1));
    Store.questions = [...questions];
  }

  @action
  duplicateQuestion(id) {
    const questions = Store.questions;
    const questionIndex = questions.findIndex((item) => item.id === id);
    const cloneAnswers = [];

    questions[questionIndex].answers.forEach((answer) => {
      cloneAnswers.push({
        correct: answer.correct,
        id: this.newId,
        order: answer.order,
        text: answer.text
      });
    });

    const clone = {
      answers: cloneAnswers,
      id: this.newId,
      text: questions[questionIndex].text,
      mandatory: questions[questionIndex].mandatory,
      order: 0
    };

    questions.splice(questionIndex + 1, 0, clone);
    questions.map((item, i) => (item.order = i + 1));

    Store.questions = [...questions];
    return questionIndex + 1;
  }

  @action
  addProfile() {
    const newId = this.newId;

    Store.profiles = [
      ...Store.profiles,
      {
        id: newId,
        temporaryId: newId,
        name: '',
        description: '',
        answerOrder: Store.profiles.length + 1
      }
    ];
    this.checkAnswersQuantity();
  }

  @action
  removeProfile(index) {
    const profiles = Store.profiles.filter((v, i) => i !== index);

    profiles.map((item, i) => (item.answerOrder = i + 1));
    Store.profiles = [...profiles];
    this.removeRelatedProfileAnswer(index);
  }

  removeRelatedProfileAnswer(index) {
    const questions = Store.questions;

    if (questions.length) {
      questions.map((question) => {
        const removedCorrect = question.answers[index].correct;

        question.answers.splice(index, 1);
        if (removedCorrect) {
          question.answers[0].correct = true;
        }
        return question;
      });
      Store.questions = [...questions];
    }
  }

  @action
  duplicateProfile(index) {
    const profiles = Store.profiles;
    const profileIndex = profiles.findIndex((v, i) => i === index);
    const newId = this.newId;
    const clone = {
      answerOrder: 0,
      id: newId,
      temporaryId: newId,
      name: profiles[profileIndex].name,
      imageId: profiles[profileIndex].imageId,
      description: profiles[profileIndex].description
    };

    profiles.splice(profileIndex + 1, 0, clone);
    profiles.map((item, i) => (item.answerOrder = i + 1));

    Store.profiles = [...profiles];
    this.checkAnswersQuantity();
    return profileIndex + 1;
  }
  // #endregion
}

export default new QuizService();
